/**
 * `OCSTransform` — carry an OCS-PLANAR entity through a 3D affine and
 * re-express the result in the TRANSFORMED plane's own OCS.
 *
 * # The defect this module exists to close
 *
 * A 2D heavy `POLYLINE` (and every other OCS-planar kind) stores its vertex
 * `x`/`y` as OBJECT coordinates in the plane of its group-210 extrusion, at the
 * entity's ELEVATION along that normal (Autodesk DXF reference, "OCS and
 * Extrusion Direction"). {@link ocsToWcs} / {@link computeOcsAxes} already own
 * the READ direction of that rule — `heavyPolylineWcsResolver`,
 * `wcsBoundsLeaf.ts::heavyPolylineLocal` and the vector-PDF plot all lift
 * through it.
 *
 * An EDIT is the other direction, and applying a WCS affine straight to the
 * stored OCS numbers is simply the wrong composition. With extrusion
 * `(0, 0, -1)` the lift is an X mirror, so a WCS `+10 X` MOVE written onto the
 * stored coordinates renders as `-10 X`; a tilted normal additionally corrupts
 * the plane itself and every direction expressed inside it. Two call sites had
 * exactly that bug (@codex round 4):
 *
 *   - `command-engine/geometryTransforms.ts`'s `Polyline` arm — MOVE / ROTATE /
 *     MIRROR / SCALE / ARRAY of a top-level 2D heavy polyline;
 *   - `apps/web/…/scene3d/adapters/typed/transformBlockChild.ts`'s `Polyline`
 *     arm — the block-child clone, which mapped the raw OCS vertex through the
 *     INSERT while PRESERVING the extrusion, so `polylineFromEntity` lifted
 *     AFTERWARDS. Those two orders do not commute, so the child rendered
 *     somewhere `wcsBoundsBlock.ts` (which applies the child's OCS BEFORE the
 *     INSERT) did not.
 *
 * # The shape, from the reference implementation
 *
 * `ezdxf.math.OCSTransform` (`ezdxf/math/_matrix44.py` + `ezdxf/entities/…`):
 *
 * ```python
 * self.old_ocs = OCS(extrusion)
 * new_extrusion, _ = transform_extrusion(extrusion, m)
 * self.new_ocs = OCS(new_extrusion)
 * def transform_vertex(self, v):    # OCS -> OCS
 *     return self.new_ocs.from_wcs(self.m.transform(self.old_ocs.to_wcs(v)))
 * def transform_direction(self, d):
 *     return self.new_ocs.from_wcs(self.m.transform_direction(self.old_ocs.to_wcs(d)))
 * ```
 *
 * …and `ezdxf.math.transform_extrusion` is
 * `normalize(cross(m.transform_direction(ocs.ux), m.transform_direction(ocs.uy)))`,
 * i.e. the new plane normal is the cross product of the IMAGES of the old OCS
 * x/y axes — which is the identity `(M·a) × (M·b) = det(M)·M⁻ᵀ·(a × b)` applied
 * to `ux × uy = n`. `ezdxf.Polyline.transform` then writes the new elevation
 * from the transformed vertices and `dxf.extrusion = ocs.new_extrusion`.
 *
 * This module is that object, with two deliberate differences, both documented
 * on the members below: the affine is PROBED from a single point map (so a
 * caller cannot desync the position map from the direction map), and the new
 * elevation is taken from the plane's own origin rather than from vertex 0.
 *
 * # Autodesk wins over ezdxf on one point: the DEFAULT extrusion
 *
 * {@link ocsPlanarTransform} returns `null` for an absent / `(0,0,1)`
 * extrusion, so the caller keeps its existing code path BIT-EXACTLY. ezdxf
 * would run the general path there too and record a MIRROR as
 * `extrusion = (0,0,-1)` with the vertices and bulges left alone. That is
 * geometrically identical, but it is not what AutoCAD writes: MIRROR of an
 * ordinary +Z polyline produces an ordinary +Z polyline with reflected
 * coordinates and negated bulges. CLAUDE.md's source order puts Autodesk above
 * ezdxf, and a representation change on the overwhelmingly common planar case
 * would also churn every ordinary drawing, so the general path is reserved for
 * the entities that genuinely need it.
 *
 * # Why the handedness does NOT reach the bulges here
 *
 * A caller that mirrors a DEFAULT-extrusion polyline must negate its bulges —
 * the arc's side of the chord is expressed in a plane that did not move. On the
 * general path the plane DOES move: the new normal is `normalize(ux × uy)`, so
 * `(ux, uy)` is a right-handed pair for it BY CONSTRUCTION, and therefore
 * {@link OcsPlanarTransform.linear} always has a POSITIVE determinant — the
 * reflection has been absorbed into the group-210 normal. Negating the bulges
 * as well would apply the flip twice. This is ezdxf's rule
 * (`Polyline.transform` touches no bulge at all) and it is verified in
 * `ocsPlanarTransform.metamorphic.test.ts` by resolving both representations
 * back to WCS.
 *
 * # KNOWN RESIDUAL — an anisotropic stretch of a TILTED plane (S10-XFORM)
 *
 * WHEN. Only when the affine's restriction to the entity's own plane is not a
 * similarity, i.e. an ANISOTROPIC stretch of a TILTED plane. Per caller:
 *   - the command path embeds its 2D edit as `M = blockdiag(L, 1)`. MOVE,
 *     ROTATE-about-Z and MIRROR are isometries of R³, so their restriction to
 *     ANY plane is an isometry and NOTHING below applies to them. SCALE is
 *     `diag(f, f, 1)`, a similarity of R³ only for `|f| = 1` — so scaling a
 *     TILTED polyline stretches its plane anisotropically, the more so the
 *     further `|f|` is from 1. (AutoCAD's own SCALE is a 3D-uniform
 *     `diag(f, f, f)` about the base point, under which none of this arises;
 *     the 2D-only embedding is this command layer's pre-existing contract, not
 *     something decided here.)
 *   - the block path's INSERT is anisotropic whenever `|scaleX| ≠ |scaleY|` —
 *     the repo-wide S10-XFORM residual its own module header records.
 *
 * WHAT DEGRADES — two separate things, and the first one is NOT only the bow:
 *
 *  1. VERTEX POSITIONS, for a large `|f|`. An anisotropic stretch flattens the
 *     plane towards the stretch's own plane, and once the image normal comes
 *     within {@link isDefaultExtrusion}'s 1e-9 of `(0,0,1)` the group 210 is
 *     canonicalised AWAY (see {@link OcsPlanarTransform.extrusion}) — the
 *     remaining tilt is below the resolution at which this repo stores a plane
 *     at all, so it is not rounded, it is DISCARDED, and no later edit can
 *     bring it back. The drawn error that leaves behind is bounded by
 *     `1e-9 · |f| · (in-plane extent)`, so a scale-up/scale-down round trip of
 *     a near-`(0,0,1)` polyline does not return exactly where it started.
 *     `geometryTransforms.polylineOcs.metamorphic.test.ts` names this case,
 *     measures it and excludes it from the round-trip property by that exact
 *     mechanism. OUTSIDE that band positions are exact to ordinary rounding
 *     (measured worst 2e-10 of the suite tolerance over 50 000 generated
 *     cases) — but see {@link conditionedProbeRadius}, whose probe radius is
 *     what keeps them there; a unit-radius finite difference silently gave up
 *     six of those digits.
 *
 *  2. THE BOW. {@link OcsPlanarTransform.linear} is exact for every affine, but
 *     a BULGE can only describe a CIRCULAR arc, so an anisotropic in-plane
 *     image hands the arc a shape a group 42 cannot spell. The authored bulge
 *     is carried through verbatim and the arc is re-drawn as a circle in the
 *     new plane rather than as the ellipse the affine actually produces.
 *
 * WHY IT IS STILL THE RIGHT TRADE. Before this module, the affine was applied
 * straight to the stored OCS numbers: the entity — endpoints included — was put
 * somewhere ELSE entirely (under `(0,0,-1)` a `+10 X` MOVE drew as `-10 X`),
 * and a scale-up/scale-down round trip of that wrong position was near-exact.
 * Correct placement under every edit, with a bounded loss in one degenerate
 * corner of one command, beats an exact round trip of the wrong answer.
 *
 * COMPLETION PLAN. Explode-to-LINE+ELLIPSE — what AutoCAD does, and what ezdxf
 * signals with `NonUniformScalingError` rather than writing an approximation —
 * removes BOTH: an ELLIPSE carries its own plane and axes, so neither the bow
 * nor the plane has to survive inside a group-42/group-210 pair. It belongs to
 * the same S10-XFORM work item as the existing non-uniform-INSERT bulge shear.
 *
 * DONE FOR ONE CONSUMER (@codex round 18, FINDING A). The BLOCK-CHILD RENDER
 * path no longer reaches residual 2 at all: a bulged 2D heavy `POLYLINE` child
 * under a NON-SIMILARITY insert is decomposed BEFORE the clone is built —
 * `apps/web/.../scene3d/adapters/typed/blockBulgeExplode.ts` builds each arc in
 * the child's OWN plane and lifts it out as an `Ellipse` piece carrying its own
 * centre, axes and group 210 (or, when the run carries a width band or a
 * group-39 sweep, rebuilds the run with its arcs already walked in that plane).
 * The residual stands as written for every OTHER caller of this module — a
 * top-level MOVE / SCALE of a tilted bulged polyline still carries its group 42
 * through verbatim — so the completion plan above is the remaining work, not a
 * finished one.
 */

import { cross, dot, length, normalize, type Vec3 } from "./vec3";
import {
  computeOcsAxes,
  isDefaultExtrusion,
  ocsToWcs,
  type Wcs3,
} from "./wcsBoundsMath";

/** A WCS point map. MUST be affine — it is probed, not sampled. */
export type OcsPointTransform = (p: Wcs3) => Wcs3;

/** The 2×2 map an OCS-planar direction takes from the OLD plane to the NEW one. */
export interface OcsPlaneLinear {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
}

/** One entity's OCS → affine → OCS pipeline, built once per entity. */
export interface OcsPlanarTransform {
  /**
   * The new group-210 normal, already CANONICALISED: `undefined` when the image
   * plane is the WCS default, so the caller clears a now-redundant group 210
   * instead of writing `(0,0,1)` explicitly. That canonicalisation is what
   * makes the rule its own inverse — the same reasoning
   * `geometryTransforms.ts::spreadSplineExtrusion` records for the SPLINE
   * normal (its round-5 finding 4).
   */
  readonly extrusion: Wcs3 | undefined;
  /**
   * The new elevation — the image plane's signed distance along the new normal.
   *
   * Taken from the image of the plane's own OCS origin `(0, 0, elevation)`
   * rather than from `vertices[0]` (ezdxf's choice): every vertex of the plane
   * maps to this same value mathematically, so sourcing it from a vertex only
   * adds that vertex's rounding and makes an entity's plane depend on which
   * record happens to be first. Callers write this ONE value into the entity
   * elevation AND into every vertex's `z`, so the stored plane is exactly
   * consistent instead of consistent to within a ULP.
   */
  readonly elevation: number;
  /**
   * The in-plane linear map, old OCS x/y → new OCS x/y. `det > 0` always (see
   * the module header). Exposed so a caller maps its own stored DIRECTIONS —
   * a `VERTEX` group-50 curve-fit tangent, a hatch pattern angle — through the
   * SAME basis pair the vertices went through. Mapping a direction through the
   * raw WCS affine while the plane is re-expressed here would desync the two.
   */
  readonly linear: OcsPlaneLinear;
  /** One OCS vertex → the new OCS. `z` is {@link elevation} for every vertex. */
  vertex(x: number, y: number): Wcs3;
  /** One in-plane OCS direction → the new OCS (linear part only). */
  direction(dx: number, dy: number): { x: number; y: number };
}

/**
 * ezdxf `transform_extrusion` — the image of an OCS normal under the affine
 * `transformPoint`, as a UNIT vector.
 *
 * `null` when the image is degenerate (the affine collapses the plane to a line
 * or a point, or the input normal is non-finite): no normal is invented and no
 * `NaN` escapes. Callers keep their prior value in that case, which is what a
 * degenerate edit already means for every other field.
 */
export function transformOcsNormal(
  extrusion: Vec3 | undefined,
  transformPoint: OcsPointTransform,
): Wcs3 | null {
  const probe = probeImagePlane(extrusion, transformPoint);
  return normalOfImagePlane(probe.ux, probe.uy);
}

/**
 * The RADIUS at which {@link probeImagePlane} evaluates its finite difference,
 * chosen so the subtraction does not cancel away the answer's leading digits.
 *
 * # The conditioning trap this exists to close
 *
 * `L(v) = fn(v) − fn(0)` is EXACT for an affine `fn` in real arithmetic, but in
 * doubles it subtracts two numbers of magnitude `|fn(0)|` to produce one of
 * magnitude `|L(v)|`, so it loses `log10(|fn(0)| / |L(v)|)` decimal digits. A
 * SCALE about a base point far from the WCS origin is exactly that shape:
 * `fn(p) = base + f·(p − base)` puts every image within `|f|` of
 * `base·(1 − f)`, so at `base = (−10000, 0)` and `f = 1/1000` both `fn(ax)` and
 * `fn(0)` are ≈ −9990 while their difference is ≈ 0.001 — SEVEN digits gone,
 * leaving `ux` with ~1e-9 of RELATIVE error.
 *
 * That error is not cosmetic: it tilts `normalize(ux × uy)` by ~3e-10 rad, and
 * {@link OcsPlanarTransform.elevation} multiplies a plane tilt by the distance
 * from the WCS origin to the plane's own origin (~1e4 here), so the re-expressed
 * plane came out ~3e-6 out of position — 2.6× over the 1e-6 tolerance the
 * command-level metamorphic suite holds SCALE ∘ SCALE⁻¹ to, and the counter-
 * example `geometryTransforms.polylineOcs.metamorphic.test.ts` now pins by name
 * (extrusion `(0.4, −0.2, 0.9)`, base `(−10000, 0)`, factor 1000).
 *
 * # The fix, and why it is not a fudge
 *
 * For an AFFINE map — which this module's contract already requires, see
 * {@link OcsPointTransform} — `L(v) = (fn(r·v) − fn(0)) / r` for EVERY `r ≠ 0`:
 * the same quantity, evaluated where the subtraction is well conditioned.
 * Picking `r = |fn(0)| / min(|L(ax)|, |L(ay)|)` brings the SMALLER difference —
 * the one that cancels first — out at the magnitude of the operands themselves,
 * i.e. no cancellation at all, and merely overshoots the larger one, which has
 * no cancellation left to give. It is a change of evaluation point, NOT a
 * tolerance, an epsilon or an approximation.
 *
 * Sizing off the LARGER image instead is the trap `diag(f, f, 1)` walks into:
 * it leaves one axis image at `|f|` and the other at its out-of-plane component
 * (~1), so at `f = 1e-6` a max-sized radius still costs the small axis six
 * digits — measured 5.8e-11 of angular error against 5.6e-17 for the min-sized
 * one. Both forms are pinned by name in `ocsPlanarTransform.metamorphic.test.ts`.
 *
 * `1` (probe unwidened) whenever the unit probe is already well conditioned —
 * `fn(0)` at or near the WCS origin, or a map whose images already separate by
 * more than `|fn(0)|` — and for every degenerate input, since `0`, `Infinity`
 * and `NaN` all fail the finite/`> 1` test. Dividing by an exact `1` is exact,
 * so that path is BIT-IDENTICAL to the plain difference.
 *
 * # What it does NOT fix
 *
 * Only cancellation VISIBLE in `fn`'s outputs. A map that cancels internally
 * against a constant it never exposes is beyond any black-box probe: SCALE by
 * exactly 1 about `base` computes `base + (p − base)·1`, which is the identity
 * in real arithmetic and returns `fn(0) = 0`, so there is nothing here to see —
 * yet it has already rounded `p` to `ulp(base)`. Measured worst normal error
 * over 30 000 generated cases at the command layer's own parameter ranges
 * (`|base| ≤ 1e4`, `1e-3 ≤ |f| ≤ 1e3`): 9.7e-13, ALL of it that residue,
 * against 4.9e-10 for the unit probe. Nor can any single radius beat the image
 * vector's own anisotropy — a component `c` of an image of norm `|u|` is not
 * recoverable to better than `eps·|u|/|c|` — which is why the radius is sized
 * off the smaller image rather than per-axis: the per-axis split buys nothing a
 * shared minimum does not already have.
 */
function conditionedProbeRadius(originLen: number, span: number): number {
  // Every degenerate input already lands on `1` through the finite/`> 1` test:
  // `0 / span` is 0, `originLen / 0` is `Infinity`, `0 / 0` and any `NaN` input
  // are `NaN`. An extra `originLen > 0 || span > 0` guard in front would be
  // unreachable — it was here and every mutation of it survived.
  const radius = originLen / span;
  // Stryker disable next-line EqualityOperator: `>` → `>=` is equivalent — at
  // `radius === 1` both branches yield the value 1, and `image(v, 1)` divides
  // by an exact 1 either way, so no input can observe the difference.
  return Number.isFinite(radius) && radius > 1 ? radius : 1;
}

/**
 * The OLD OCS basis plus the IMAGES of its two in-plane axes under
 * `transformPoint`.
 *
 * A direction is the difference of two point images, so the affine's linear
 * part is recovered by probing the origin and subtracting — the 3D
 * generalisation of `geometryTransforms.ts::linearPart`. ONE truth for both
 * entry points below, so the normal a caller gets from
 * {@link transformOcsNormal} is always the normal
 * {@link ocsPlanarTransform} re-expresses into.
 *
 * The difference is taken at {@link conditionedProbeRadius} — see there for the
 * catastrophic cancellation a unit-radius probe suffers when the affine's fixed
 * point is far from the WCS origin, and for why the radius is sized off the
 * SMALLER of the two images.
 *
 * There is deliberately NO finiteness guard on the widened pair. A non-degenerate
 * unit probe already bounds the radius at `≈ 1/eps`: `|u| ≳ eps·|fn(0)|` is
 * exactly what it takes for `fn(v) − fn(0)` not to annihilate, and the radius is
 * `|fn(0)| / |u|`. At that bound `radius · v` stays inside the finite range for
 * a unit `v`, and the widened difference lands at `≈ |fn(0)|`, finite by the
 * same premise — so every map that could overflow here has already produced a
 * degenerate unit probe. A guard would therefore be unreachable code that no
 * test can pin (it was written, and every mutation of it survived); a non-finite
 * image still cannot escape, because {@link normalOfImagePlane} answers `null`
 * for one, which is this module's documented degenerate result.
 */
function probeImagePlane(
  extrusion: Vec3 | undefined,
  transformPoint: OcsPointTransform,
): { oldAxes: ReturnType<typeof computeOcsAxes>; ux: Wcs3; uy: Wcs3 } {
  const oldAxes = computeOcsAxes(extrusion);
  const origin = transformPoint({ x: 0, y: 0, z: 0 });
  const image = (v: Wcs3, radius: number): Wcs3 => {
    const q = transformPoint({
      x: v.x * radius,
      y: v.y * radius,
      z: v.z * radius,
    });
    return {
      x: (q.x - origin.x) / radius,
      y: (q.y - origin.y) / radius,
      z: (q.z - origin.z) / radius,
    };
  };
  // The SMALLER image sets the radius: it is the one whose difference cancels
  // first, and over-widening the larger one costs nothing (its difference just
  // overshoots `|origin|`, which has no cancellation left to give).
  const radius = conditionedProbeRadius(
    length(origin),
    Math.min(length(image(oldAxes.ax, 1)), length(image(oldAxes.ay, 1))),
  );
  return {
    oldAxes,
    ux: image(oldAxes.ax, radius),
    uy: image(oldAxes.ay, radius),
  };
}

/**
 * `normalize(ux × uy)`, or `null` when the pair is degenerate.
 *
 * The degeneracy test is RELATIVE (`|ux × uy| ≤ 1e-12 · |ux| · |uy|`, i.e. the
 * sine of the angle between the images): an absolute threshold would reject a
 * perfectly good plane spanned by 1e-7-scale axes and accept a collinear pair
 * of 1e6-scale ones. A non-finite input fails the comparison and returns `null`
 * too, which is the same answer for the same reason.
 */
function normalOfImagePlane(ux: Wcs3, uy: Wcs3): Wcs3 | null {
  const n = cross(ux, uy);
  if (!(length(n) > 1e-12 * length(ux) * length(uy))) return null;
  return normalize(n);
}

/**
 * Build the OCS → affine → OCS pipeline for ONE planar entity, or `null` when
 * there is no work to do.
 *
 * `null` means "use your existing path, unchanged", and it is returned for:
 *   - an absent / `(0,0,1)` extrusion — the bit-exact common case, see the
 *     module header's Autodesk-over-ezdxf note;
 *   - a degenerate affine ({@link normalOfImagePlane}).
 *
 * `transformPoint` is the ONLY input; its linear part is recovered by
 * {@link probeImagePlane}, which is legitimate because every map reaching
 * either call site is affine (`translatePoint` / `rotatePoint` / `scalePoint` /
 * `mirrorPoint` and their compositions in the command path;
 * `transformBlockChildPoint` in the block path). Taking a SECOND function for
 * directions would let a caller pass a direction map that does not match its
 * point map, which is precisely the desync this design forbids.
 */
export function ocsPlanarTransform(
  extrusion: Vec3 | undefined,
  elevation: number,
  transformPoint: OcsPointTransform,
): OcsPlanarTransform | null {
  if (isDefaultExtrusion(extrusion)) return null;
  const { oldAxes, ux, uy } = probeImagePlane(extrusion, transformPoint);
  const normal = normalOfImagePlane(ux, uy);
  if (normal === null) return null;
  const newAxes = computeOcsAxes(normal);
  // The columns of the in-plane map ARE the images of the old axes, read off in
  // the new basis — so `vertex` and `direction` cannot disagree about which
  // plane they are speaking in.
  const linear: OcsPlaneLinear = {
    a: dot(ux, newAxes.ax),
    b: dot(ux, newAxes.ay),
    c: dot(uy, newAxes.ax),
    d: dot(uy, newAxes.ay),
  };
  const vertex = (x: number, y: number): Wcs3 => {
    const wcs = transformPoint(ocsToWcs({ x, y, z: elevation }, oldAxes));
    return {
      x: dot(wcs, newAxes.ax),
      y: dot(wcs, newAxes.ay),
      z: dot(wcs, newAxes.az),
    };
  };
  // The image plane's own signed distance along the new normal — see
  // `OcsPlanarTransform.elevation` for why this and not `vertices[0].z`.
  const planeZ = vertex(0, 0).z;
  return {
    extrusion: isDefaultExtrusion(normal) ? undefined : normal,
    elevation: planeZ,
    linear,
    vertex: (x, y) => {
      const p = vertex(x, y);
      return { x: p.x, y: p.y, z: planeZ };
    },
    direction: (dx, dy) => ({
      x: linear.a * dx + linear.c * dy,
      y: linear.b * dx + linear.d * dy,
    }),
  };
}
